import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody } from '@americanexpress/dls-react';
import { Link } from '@americanexpress/one-app-router';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { IconChevronRight } from '@/components/icons';
import { cardArtMapping } from '@/constants/productMapping';
import {
  getCardArtUrl,
  getBasePathFromLocation,
  resizeSelectElementToCurrentValue,
  getProduct,
} from '@/helpers/utils';
import { getLocaleData } from '@/selectors/localeSelectors';
import { getMycaLoginFlag } from '@/selectors/experimentSelectors';
import { getIsNotLoggedIn } from '@/selectors/authSelectors';
import { getUserWallet } from '@/selectors/walletSelector';
import { supportedCountries } from '@/constants/supportedLocales';
import { useResizeSelectElement } from '@/hooks/useResizeSelectElement';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { useRouteParams } from '@/context/routeParamsContext';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { useModal } from '@/context/modalContext';
import LoginCard from '../LoginCard';
import Wallet from '../Wallet';

const SelectCardModal = () => {
  const baseOneCmsImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const { locale } = useSelector(getLocaleData);
  const { enabled: isMycaLoginEnabled } = useSelector(getMycaLoginFlag);
  const notLoggedIn = useSelector(getIsNotLoggedIn);
  const [selectedLocale, setSelectedLocale] = useState(
    locale ? locale.toLocaleLowerCase() : 'en-us'
  );
  const { product: productFromParams } = useRouteParams();
  const { customTrack } = useAnalyticsContext();
  const { closeModal, modalName } = useModal();
  const { selectCard, productDisplayNames } = useCommonContent();

  let wallet = useSelector(getUserWallet);
  if (!notLoggedIn) {
    wallet = wallet
      .filter((card) => card.hasLoungeBenefit)
      .map((card) => {
        const loungeFinderProduct = getProduct(card?.product?.type);
        return {
          ...card,
          loungeFinderProduct,
        };
      });
  }

  const isOpen = modalName !== '';
  useResizeSelectElement('#country-select', isOpen);

  const basePath = getBasePathFromLocation(
    selectedLocale,
    window.location.pathname
  );

  const handleSelectCountry = (event) => {
    setSelectedLocale(event.target.value);
    resizeSelectElementToCurrentValue(event.target);
    customTrack({
      event,
      detail: `${locale} -> ${event.target.value}`,
      element: 'card picker country selector',
    });
  };

  const handleSelectCard = (event, newProduct) => {
    closeModal();
    customTrack({
      event,
      detail: `${productFromParams} -> ${newProduct}`,
      element: 'card picker card selector',
    });
  };

  return (
    <Modal
      id="select-card-modal"
      onClose={closeModal}
      innerContainerClassName="tw-max-w-screen-sm md_tw-max-w-screen-md tw-w-full tw-m-0 sm_tw-m-4"
    >
      <ModalHeader id="select-card-modal-header">
        <h3>{selectCard?.header}</h3>
      </ModalHeader>
      <ModalBody
        className="tw-min-h-75dvh tw-max-h-75vh-important sm_tw-min-h-full"
        style={{ paddingInline: '1rem' }}
      >
        {isMycaLoginEnabled && notLoggedIn && <LoginCard />}
        {isMycaLoginEnabled && !notLoggedIn && (
          <Wallet wallet={wallet} handleClick={handleSelectCard} />
        )}
        <div className="sm_tw-flex sm_tw-items-center">
          <label
            htmlFor="country-select"
            className="tw-mb-2 tw-mr-1 tw-inline tw-font-sans tw-text-base tw-font-bold tw-text-gray-700"
          >
            {selectCard?.selectLabel}{' '}
          </label>
          <select
            name="country-select"
            id="country-select"
            aria-label="Select a country"
            className="tw-mr-1 tw-appearance-none tw-bg-transparent tw-bg-icon-chevron-down tw-bg-right-top-1dot25 tw-bg-no-repeat tw-pb-1dot5 tw-font-sans tw-font-bold tw-text-blue-bright-shade-600-s8"
            value={selectedLocale.toLocaleLowerCase()}
            onChange={handleSelectCountry}
          >
            {supportedCountries
              .filter((country) => country.showInCardPicker)
              .map(({ locale: countryLocale, name, multiLanguage }) => {
                const langTag = countryLocale.split('-')[0];
                const displayName = multiLanguage
                  ? `${name} - ${langTag.toUpperCase()}`
                  : name;
                return (
                  <option key={countryLocale} value={countryLocale}>
                    {displayName}
                  </option>
                );
              })}
          </select>
        </div>
        <div className="tw-flex tw-flex-col">
          {supportedCountries
            .find(
              (country) => country.locale === selectedLocale.toLocaleLowerCase()
            )
            .products.map((localeProduct) => {
              const productDisplayName =
                productDisplayNames[localeProduct]?.selectCardDisplayName;
              const imgSrc = getCardArtUrl({
                baseOneCmsImgUrl,
                product: cardArtMapping[localeProduct],
                locale: selectedLocale,
              });
              const cardInWallet = wallet.some(
                (card) =>
                  card.loungeFinderProduct === localeProduct &&
                  card.locale.toLowerCase() === selectedLocale
              );

              // do not show the card if the user has it in their wallet
              if (cardInWallet) {
                return null;
              }
              return (
                <Link
                  key={localeProduct}
                  to={{
                    pathname: `${basePath}/${localeProduct}`,
                    search: window.location.search,
                  }}
                  className="tw-flex tw-items-center tw-gap-4 tw-px-2 tw-py-4 hover_tw-rounded-lg hover_tw-bg-gray-200"
                  onClick={(e) => handleSelectCard(e, localeProduct)}
                >
                  <img
                    src={imgSrc}
                    alt="card-art"
                    className="tw-h-11 tw-w-17dot5"
                  />
                  <div className="tw-flex tw-flex-col">
                    <p className="tw-text-gray-800">{productDisplayName}</p>
                    {localeProduct === 'the-centurion-card' && (
                      <p className="tw-text-sm tw-text-gray-600">
                        {selectCard?.centurionMessage}
                      </p>
                    )}
                  </div>
                  <IconChevronRight
                    size="sm"
                    className="tw-ml-auto tw-fill-gray-600"
                  />
                </Link>
              );
            })}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default SelectCardModal;
